import React, { useCallback } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { useLocaleDecimalInput } from '../../hooks/useLocaleDecimalInput';

export interface DecimalNumberInputProps extends Omit<
  TextFieldProps,
  'value' | 'onChange' | 'type' | 'defaultValue'
> {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
}

const DecimalNumberInput: React.FC<DecimalNumberInputProps> = ({
  value,
  onChange,
  decimalPlaces = 2,
  min,
  max,
  onFocus,
  onBlur,
  inputProps,
  ...textFieldProps
}) => {
  const {
    displayValue,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
  } = useLocaleDecimalInput({
    value,
    onChange,
    decimalPlaces,
    min,
    max,
  });

  const handleInputFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleFocus();
      onFocus?.(event);
    },
    [handleFocus, onFocus],
  );

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleBlur();
      onBlur?.(event);
    },
    [handleBlur, onBlur],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event.target.value);
    },
    [handleChange],
  );

  return (
    <TextField
      {...textFieldProps}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      inputProps={{
        ...inputProps,
        autoComplete: inputProps?.autoComplete ?? 'off',
      }}
    />
  );
};

export default DecimalNumberInput;
